import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle, Search } from 'lucide-react';
import { Button } from '../components/ui/button';

const FAQPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is AutomateSEO.ai?",
          a: "AutomateSEO.ai is India's first fully automated AI platform for SEO, local rankings, Google My Business, social media marketing, and content creation. We help businesses automate their entire digital marketing workflow using advanced AI technology, saving 40+ hours weekly."
        },
        {
          q: "How do I get started?",
          a: "Getting started is easy! Sign up for a free 14-day trial (no credit card required), connect your Google My Business, website, and social media accounts, and our AI will automatically start optimizing your SEO, creating content, and managing your social media. Results typically appear within 7-14 days."
        },
        {
          q: "Do I need technical knowledge to use AutomateSEO.ai?",
          a: "No! AutomateSEO.ai is designed for non-technical users. Our AI handles all the complex technical aspects of SEO and digital marketing. You simply connect your accounts, set your preferences, and let our AI do the work. No coding, no technical expertise required."
        },
        {
          q: "How long does setup take?",
          a: "Setup takes approximately 5-10 minutes. You'll connect your accounts (Google My Business, website, social media), answer a few questions about your business, and our AI starts working immediately. Most businesses are fully operational within 15 minutes."
        }
      ]
    },
    {
      category: "Pricing & Billing",
      questions: [
        {
          q: "How much does AutomateSEO.ai cost?",
          a: "We offer flexible pricing: Starter Plan (₹2,999/month) for small businesses and startups, Pro Plan (₹3,999/month) for growing businesses and e-commerce brands. All plans include a 3-day free trial available on request with no credit card required."
        },
        {
          q: "Is there a free trial?",
          a: "Yes! We offer a 14-day free trial with full access to all features. No credit card required. You can cancel anytime during the trial without any charges. After the trial, choose a plan or your account reverts to a free tier with limited features."
        },
        {
          q: "What's your refund policy?",
          a: "We offer a 90-day results guarantee. If you don't see measurable improvements in your traffic, rankings, or engagement within 90 days of active use, we'll provide a full refund - no questions asked. See our complete Refund Policy for details."
        },
        {
          q: "Can I change or cancel my plan?",
          a: "Yes! You can upgrade, downgrade, or cancel your plan anytime. Changes take effect at the end of your current billing cycle. If you cancel, you'll retain access until the end of your paid period. Downgrades are prorated."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards (Visa, Mastercard, Amex), debit cards, UPI, net banking, and digital wallets through Razorpay. For enterprise plans, we also accept bank transfers and invoicing."
        }
      ]
    },
    {
      category: "Features & Functionality",
      questions: [
        {
          q: "What does SEO automation include?",
          a: "Our SEO automation includes: automated keyword research, on-page optimization, technical SEO audits, content optimization, internal linking, meta tag generation, schema markup, rank tracking, competitor analysis, and monthly reporting. All running 24/7 automatically."
        },
        {
          q: "How does social media automation work?",
          a: "Our AI analyzes your brand, creates content calendars, generates posts (text and images), schedules optimal posting times, manages multiple platforms (Instagram, Facebook, LinkedIn, Twitter), responds to engagement, and provides analytics - all automatically."
        },
        {
          q: "What is Google My Business optimization?",
          a: "We automatically optimize your GMB profile with: regular posts, review management, Q&A monitoring, photo optimization, business hours updates, service listings, citation building, and local ranking improvement. Everything runs on autopilot."
        },
        {
          q: "Can I create custom content with AI?",
          a: "Yes! Our platform includes AI content generation for blog posts, social media, images (DALL-E 3), videos (RunwayML), product descriptions, ad copy, and more. You can customize tone, style, and output to match your brand perfectly."
        },
        {
          q: "How many social media accounts can I connect?",
          a: "Starter Plan: 3 accounts. Pro Plan: 10 accounts. You can connect Instagram, Facebook, LinkedIn, Twitter, YouTube, and Pinterest across all plans."
        }
      ]
    },
    {
      category: "Results & Performance",
      questions: [
        {
          q: "How quickly will I see results?",
          a: "Most businesses see initial improvements within 7-14 days (increased engagement, better local visibility). Significant SEO results typically appear in 30-60 days (ranking improvements, traffic increases). By 90 days, most customers see 300% traffic increases and substantially improved rankings."
        },
        {
          q: "What kind of results can I expect?",
          a: "Average results: 300% organic traffic increase within 90 days, 5-10 keyword ranking improvements, 40+ hours saved weekly, 2-3x social media engagement increase, 35% more in-store/website visits. Results vary by industry, competition, and starting position."
        },
        {
          q: "How do you measure success?",
          a: "We track: organic traffic, keyword rankings, local search visibility, social media engagement, follower growth, website conversions, Google My Business views/actions, review count/rating, and overall ROI. You get detailed monthly reports with all metrics."
        },
        {
          q: "What if I don't see results?",
          a: "We offer a 90-day results guarantee. If you don't see measurable improvements, contact us for optimization review or request a full refund. We'll work with you to ensure success or provide your money back."
        }
      ]
    },
    {
      category: "Technical & Integration",
      questions: [
        {
          q: "What platforms do you integrate with?",
          a: "We integrate with: Google My Business, Google Analytics, Google Search Console, Instagram, Facebook, LinkedIn, Twitter, YouTube, Pinterest, WordPress, Shopify, WooCommerce, and 50+ other platforms. New integrations added monthly."
        },
        {
          q: "Is my data secure?",
          a: "Yes! We're GDPR compliant with enterprise-grade security: 256-bit encryption, SOC 2 compliance, regular security audits, secure data centers, and strict access controls. We never sell your data. See our Privacy Policy for complete details."
        },
        {
          q: "Do you offer API access?",
          a: "Yes! Pro plan includes API access for custom integrations. We provide comprehensive documentation, SDKs, webhooks, and developer support for seamless third-party integrations."
        },
        {
          q: "Can I export my data?",
          a: "Yes! You can export all your data anytime: content, analytics, reports, keywords, posts, and account information. Data export available in CSV, JSON, and PDF formats. Your data belongs to you."
        }
      ]
    },
    {
      category: "Support & Training",
      questions: [
        {
          q: "What support do you offer?",
          a: "We provide 24/7 support via email, phone (+91 9810535398), and live chat. Response time: Under 2 hours for all queries, under 30 minutes for urgent issues. Enterprise customers get dedicated account managers."
        },
        {
          q: "Do you offer training?",
          a: "Yes! Free resources include: video tutorials, documentation, webinars, blog guides, and case studies. Pro and Enterprise plans get personalized onboarding sessions and strategic consultation calls."
        },
        {
          q: "Is there a knowledge base?",
          a: "Yes! Our Help Center has 200+ articles, video tutorials, step-by-step guides, best practices, and troubleshooting tips covering every feature. Updated weekly with new content."
        },
        {
          q: "Can I speak to someone about my specific needs?",
          a: "Absolutely! Book a free consultation call with our team. We'll discuss your business, goals, challenges, and create a custom strategy. No sales pressure - just helpful advice. Call +91 9810535398 or email support@automateseo.ai."
        }
      ]
    }
  ];

  const filteredFAQs = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(faq =>
      faq.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">A</div>
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/help-center')}>Help Center</Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
            <HelpCircle className="w-10 h-10" />
          </div>
          <h1 className="text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">Find quick answers to common questions about AutomateSEO.ai</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="container mx-auto px-6 -mt-8">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 shadow-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-lg"
            />
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        {filteredFAQs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No FAQs found matching "{searchTerm}"</p>
            <Button onClick={() => setSearchTerm('')} className="mt-4">Clear Search</Button>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredFAQs.map((category, catIndex) => (
              <div key={catIndex}>
                <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2">
                  <span className="w-2 h-8 bg-gradient-to-b from-blue-600 to-purple-600 rounded-full"></span>
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.questions.map((faq, qIndex) => {
                    const globalIndex = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === globalIndex;
                    return (
                      <div key={qIndex} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all">
                        <button
                          onClick={() => toggleQuestion(globalIndex)}
                          className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4 text-gray-700 leading-relaxed border-t border-gray-100 pt-4">
                            {faq.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
          <p className="text-xl text-blue-100 mb-6">Our support team is here to help 24/7</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => window.location.href = 'mailto:support@automateseo.ai'}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Email Support
            </Button>
            <Button 
              onClick={() => window.location.href = 'tel:+919810535398'}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Call +91 9810535398
            </Button>
            <Button 
              onClick={() => navigate('/help-center')}
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10"
            >
              Visit Help Center
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;